import { z } from 'zod';

import { LOCALES } from '../enums.js';

import { TenantSchema } from './tenant.js';
import { UserSchema } from './user.js';

const PasswordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .max(256, 'Password must be at most 256 characters')
  .regex(/[A-Z]/, 'Password must contain an uppercase letter')
  .regex(/[a-z]/, 'Password must contain a lowercase letter')
  .regex(/[0-9]/, 'Password must contain a digit');

export const AuthSignupRequestSchema = z.object({
  email: z.string().email().toLowerCase(),
  password: PasswordSchema,
  fullName: z.string().min(1).max(200),
  tenantName: z.string().min(1).max(200),
  tenantSlug: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/),
  locale: z.enum(LOCALES).default('en'),
  baseCurrencyCode: z
    .string()
    .length(3)
    .regex(/^[A-Z]{3}$/)
    .default('USD'),
});

export type AuthSignupRequest = z.infer<typeof AuthSignupRequestSchema>;

/** Self-service password change (staff users + platform admins). */
export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: PasswordSchema,
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

export const AuthLoginRequestSchema = z.object({
  email: z.string().email().toLowerCase(),
  password: z.string().min(1),
  tenantSlug: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/)
    .optional(),
  /**
   * A paired desktop till proving which terminal it is. Sent together or not at
   * all; a wrong secret is not an error, it just means this is an ordinary
   * session rather than a till one — so a revoked pairing never leaves a
   * counter unable to sign in mid-trade.
   */
  terminalId: z.string().uuid().optional(),
  terminalSecret: z.string().min(1).max(200).optional(),
  /**
   * What the client says it is, used only when it cannot prove otherwise. The
   * till proves itself with the secret above; this exists so the field-rep app
   * and the manager desktop app can identify themselves, and it is a
   * DECLARATION, not evidence.
   *
   * `manager` is not merely descriptive: the server refuses it to anyone who
   * is neither the shop's owner nor granted `manager.access`, so declaring it
   * can only ever LOSE you a session, never gain you one.
   */
  surface: z.enum(['web', 'rep', 'manager']).optional(),
  /**
   * The declaring app's own version (the manager exe sends it). A non-owner is
   * let into the manager app only from a version that keeps backups to the
   * owner — see MANAGER_NON_OWNER_MIN_VERSION in the API.
   */
  appVersion: z.string().max(32).optional(),
});

export type AuthLoginRequest = z.infer<typeof AuthLoginRequestSchema>;

export const AuthTokenPairSchema = z.object({
  accessToken: z.string().min(1),
  expiresIn: z.number().int().positive(),
});

export type AuthTokenPair = z.infer<typeof AuthTokenPairSchema>;

export const AuthLoginResponseSchema = z.object({
  tokens: AuthTokenPairSchema,
  user: UserSchema,
  tenant: TenantSchema,
});

export type AuthLoginResponse = z.infer<typeof AuthLoginResponseSchema>;

export const AuthRefreshResponseSchema = AuthTokenPairSchema;
export type AuthRefreshResponse = z.infer<typeof AuthRefreshResponseSchema>;
